import { type ReactNode } from "react";

import { BuiPill, type IBuiPillProps } from "@btnet/blueprint-components";
import { cn } from "@btnet/utilities-core";

import { BTAvatar, type IBTAvatarProps } from "../BTAvatar/BTAvatar";
import { BTPopover } from "../BTPopover/BTPopover";

/**
 * Props for the {@link BTPill} component. Extends {@link IBuiPillProps} with
 * app-level conveniences for tooltips and avatar configuration.
 */
export interface IBTPillProps extends IBuiPillProps {
  /**
   * When provided, the pill is wrapped in a `BTPopover` that shows this content
   * on hover.
   * @default undefined
   */
  tooltip?: ReactNode;

  /**
   * Configuration for a `BTAvatar` rendered at the start of the pill. Built as
   * a size `xs` avatar with its own tooltip suppressed, then passed to
   * `BuiPill` as its `avatar`.
   * @default undefined
   */
  avatarConfig?: IBTAvatarProps;
}

/**
 * BTPill - the app-facing pill/chip wrapper around {@link BuiPill}.
 *
 * Adds two conveniences on top of the BUI primitive:
 * - `avatarConfig`: builds a `BTAvatar` (sized `xs`, tooltip suppressed) and
 *   passes it through as the pill's avatar.
 * - `tooltip`: wraps the pill in a `BTPopover` so hovering reveals the content.
 *
 * All other props are forwarded to `BuiPill`. Prefer this component over
 * `BuiPill` directly in app code.
 *
 * @param props - {@link IBTPillProps} — `BuiPill` props plus `tooltip` and `avatarConfig`.
 * @returns The rendered pill, optionally wrapped in a tooltip popover.
 * @example
 * <BTPill testid="owner-pill" text="Jane Doe" tooltip="Project owner" />
 * @example
 * <BTPill
 *   testid="assignee-pill"
 *   text="Jane Doe"
 *   avatarConfig={{ text: "Jane Doe", src: avatarUrl, testid: "avatar" }}
 *   isActionable
 *   ariaLabel="Remove assignee"
 *   onClick={handleRemove}
 * />
 */
export function BTPill(props: IBTPillProps) {
  const { testid, tooltip, avatarConfig, className } = props;
  const avatar = avatarConfig ? (
    <BTAvatar
      className="shadow-none min-w-max"
      size="xs"
      {...avatarConfig}
      testid={testid + "-avatar"}
    />
  ) : undefined;
  const pill = (
    <BuiPill {...props} className={cn("BTPill", className)} avatar={avatar} />
  );
  if (props.tooltip) {
    return (
      <BTPopover data-testid={`${testid}-tooltip`} content={tooltip}>
        {pill}
      </BTPopover>
    );
  }

  return pill;
}
